import { useState, useEffect } from 'react';
import { useOrganizationSettings } from './useOrganizationSettings';

export type MembershipMode = 'open' | 'approval_required' | 'invite_only';
export type MemberRole = 'admin' | 'member' | 'guest';

export interface MembershipSettings {
  membershipMode: MembershipMode;
  defaultMemberRole: MemberRole;
  autoApproveDomains: string[];
  registrationMessage: string;
}

export const useMembershipMode = (organizationId?: string) => {
  const { getSetting, updateSetting, loading } = useOrganizationSettings(organizationId);
  const [settings, setSettings] = useState<MembershipSettings>({
    membershipMode: 'invite_only',
    defaultMemberRole: 'member',
    autoApproveDomains: [],
    registrationMessage: '',
  });

  useEffect(() => {
    if (!loading) {
      const membershipMode = (getSetting('membership_mode') || 'invite_only') as MembershipMode;
      const defaultMemberRole = (getSetting('default_member_role') || 'member') as MemberRole;
      const autoApproveDomains = JSON.parse(getSetting('auto_approve_domains') || '[]');
      const registrationMessage = getSetting('registration_message') || '';

      setSettings(prev => {
        // Only update if values actually changed to prevent infinite loop
        if (
          prev.membershipMode !== membershipMode ||
          prev.defaultMemberRole !== defaultMemberRole ||
          JSON.stringify(prev.autoApproveDomains) !== JSON.stringify(autoApproveDomains) ||
          prev.registrationMessage !== registrationMessage
        ) {
          return {
            membershipMode,
            defaultMemberRole,
            autoApproveDomains,
            registrationMessage,
          };
        }
        return prev;
      });
    }
  }, [loading]);

  const updateMembershipMode = async (mode: MembershipMode) => {
    await updateSetting('membership_mode', mode, 'Controls how new members can join');
    setSettings(prev => ({ ...prev, membershipMode: mode }));
  };

  const updateDefaultMemberRole = async (role: MemberRole) => {
    await updateSetting('default_member_role', role, 'Default role assigned to new members');
    setSettings(prev => ({ ...prev, defaultMemberRole: role }));
  };

  const updateAutoApproveDomains = async (domains: string[]) => {
    const domainsJson = JSON.stringify(domains);
    await updateSetting('auto_approve_domains', domainsJson, 'Email domains that are automatically approved');
    setSettings(prev => ({ ...prev, autoApproveDomains: domains }));
  };

  const updateRegistrationMessage = async (message: string) => {
    await updateSetting('registration_message', message, 'Custom message shown during registration');
    setSettings(prev => ({ ...prev, registrationMessage: message }));
  };

  const shouldAutoApprove = (email: string): boolean => {
    if (settings.membershipMode !== 'approval_required') return false;
    if (!settings.autoApproveDomains.length) return false;
    
    const emailDomain = email.split('@')[1]?.toLowerCase();
    return settings.autoApproveDomains.some(domain => 
      domain.toLowerCase() === emailDomain
    );
  };

  return {
    settings,
    loading,
    updateMembershipMode,
    updateDefaultMemberRole,
    updateAutoApproveDomains,
    updateRegistrationMessage,
    shouldAutoApprove,
  };
};